import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { Select } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { scale, scaleWidth, startLeft, CusTomTimelineRow } from './mock';
import { Howl } from 'howler';
import {
  PauseOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { Captions, MessageCircle, Mic } from 'lucide-react';
import { Comment } from '../../types/typing.ts';
import RecordingPopup from './AudioRecordingPopup.tsx';
import TextToSpeechDialog from './TextToSpeechDialog';

const { Option } = Select;
export const Rates = [0.2, 0.5, 0.75, 1.0, 1.5, 2.0];

const TimelinePlayer: FC<{
  timelineState: React.MutableRefObject<TimelineState>;
  autoScrollWhenPlay: React.MutableRefObject<boolean>;
  data: CusTomTimelineRow[];
  comments: Comment[];
  setIsCommentOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onAudioRecordingComplete: (
    recordedAudioFile: File,
    duration: string
  ) => void;
}> = ({
  timelineState,
  autoScrollWhenPlay,
  data,
  comments,
  setIsCommentOpen,
  onAudioRecordingComplete,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [audio, setAudio] = useState<Howl | null>(null);
  // Ref mirror: the unmount cleanup below closes over first-render state, so
  // stopping `audio` directly would miss the Howl created on play (stale
  // closure) and music would survive navigation. The ref always holds it.
  const audioRef = useRef<Howl | null>(null);
  const [isRecordingPopupOpen, setIsRecordingPopupOpen] = useState(false);
  const [isTextToSpeechOpen, setIsTextToSpeechOpen] = useState(false);

  useEffect(() => {
    if (!timelineState.current) return;
    const engine = timelineState.current;

    const handlePlay = () => setIsPlaying(true);
    const handlePaused = () => setIsPlaying(false);
    const handleAfterSetTime = ({ time }: { time: number }) => setTime(time);
    const handleSetTimeByTick = ({ time }: { time: number }) => {
      setTime(time);
      if (autoScrollWhenPlay.current) {
        const autoScrollFrom = 500;
        const left = time * (scaleWidth / scale) + startLeft - autoScrollFrom;
        timelineState.current.setScrollLeft(left);
      }
    };

    engine.listener.on('play', handlePlay);
    engine.listener.on('paused', handlePaused);
    engine.listener.on('afterSetTime', handleAfterSetTime);
    engine.listener.on('setTimeByTick', handleSetTimeByTick);

    return () => {
      engine.listener.off('play', handlePlay);
      engine.listener.off('paused', handlePaused);
      engine.listener.off('afterSetTime', handleAfterSetTime);
      engine.listener.off('setTimeByTick', handleSetTimeByTick);
      // Stop (not just pause) so no audio survives navigation away mid-play.
      try {
        audioRef.current?.stop();
        audioRef.current?.unload();
      } catch {
        // Best-effort teardown must never break unmount.
      }
      audioRef.current = null;
    };
  }, []);

  const handlePlayOrPause = () => {
    if (!timelineState.current) return;

    if (timelineState.current.isPlaying) {
      timelineState.current.pause();
      audio?.pause();
    } else {
      timelineState.current.play({ autoEnd: true });

      const audioTrack = data.find(
        (track) => track.src && track.src.endsWith('.mp3')
      );

      if (audioTrack) {
        // Stop the previous track before starting a new one.
        try {
          audioRef.current?.stop();
          audioRef.current?.unload();
        } catch {
          // Best-effort.
        }
        const sound = new Howl({
          src: [audioTrack.src ?? ''],
          autoplay: true,
        });
        audioRef.current = sound;
        setAudio(sound);
      }
    }
  };

  const handleRateChange = (rate: number) => {
    if (!timelineState.current) return;
    timelineState.current.setPlayRate(rate);
  };

  const timeRender = (time: number) => {
    const float = (parseInt((time % 1) * 100 + '', 10) + '').padStart(2, '0');
    const min = (parseInt(time / 60 + '', 10) + '').padStart(2, '0');
    const second = (parseInt((time % 60) + '', 10) + '').padStart(2, '0');
    return <>{`${min}:${second}.${float.replace('0.', '')}`}</>;
  };

  const handleTextToSpeechComplete = (
    audioBlob: Blob,
    duration: string,
    fileName: string
  ) => {
    const file = new File([audioBlob], fileName, { type: 'audio/mp3' });
    onAudioRecordingComplete(file, duration);
  };

  return (
    <div className="timeline-player">
      <div className="play-control" onClick={handlePlayOrPause}>
        {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
      </div>
      <div className="time">{timeRender(time)}</div>
      <div className="rate-control">
        <Select
          className="timeline-rate-select"
          classNames={{ popup: { root: 'timeline-rate-select-dropdown' } }}
          size="small"
          defaultValue={1}
          style={{ width: 120 }}
          onChange={handleRateChange}
        >
          {Rates.map((rate) => (
            <Option key={rate} value={rate}>{`${
              Number.isInteger(rate) ? rate.toFixed(1) : rate
            }x`}</Option>
          ))}
        </Select>
      </div>
      <div className="controls-right">
        <div
          className={`text-to-speech-control ${
            isTextToSpeechOpen ? 'active' : ''
          }`}
          onClick={() => setIsTextToSpeechOpen(true)}
          title="Text to speech"
        >
          <Captions className="control-icon" size={18} strokeWidth={2.1} />
        </div>
        <div
          className={`audio-control ${
            isRecordingPopupOpen ? 'active' : ''
          }`}
          onClick={() => setIsRecordingPopupOpen(!isRecordingPopupOpen)}
          title="Record audio"
        >
          <Mic className="control-icon" size={18} strokeWidth={2.1} />
        </div>
        <div
          className="comment-control"
          onClick={() => setIsCommentOpen((prev) => !prev)}
          title="Comments"
        >
          <MessageCircle className="control-icon" size={18} strokeWidth={2.1} />
          {comments.length > 0 && (
            <div className="comment-badge">{comments.length}</div>
          )}
        </div>
      </div>

      <RecordingPopup
        isOpen={isRecordingPopupOpen}
        onClose={() => setIsRecordingPopupOpen(false)}
        onRecordingComplete={onAudioRecordingComplete}
      />

      <TextToSpeechDialog
        isOpen={isTextToSpeechOpen}
        onClose={() => setIsTextToSpeechOpen(false)}
        onAudioGenerated={handleTextToSpeechComplete}
      />
    </div>
  );
};

export default TimelinePlayer;
